import { FormEvent, useEffect, useRef, useState } from "react";
import {
  ArrowLeft,
  ChevronDown,
  File,
  FileImage,
  Film,
  Paperclip,
  Plus,
  TicketCheck,
  X,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  SelectOption,
  TicketAttachment,
  categoryOptions,
  createTicketApi,
  makeAttachmentFromFile,
  replyToTicket,
  severityOptions,
  subjectOptions,
  uploadTicketImage,
} from "./supportTicketsData";
import "./SupportPages.css";

type PendingAttachment = {
  file: File;
  preview: TicketAttachment;
};

interface SupportCardSelectProps {
  label: string;
  placeholder: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
}

const SupportCardSelect = ({
  label,
  placeholder,
  value,
  options,
  onChange,
}: SupportCardSelectProps) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const onMouseDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", onMouseDown);
    return () =>
      document.removeEventListener("mousedown", onMouseDown);
  }, []);

  const selected = options.find((option) => option.value === value);

  return (
    <div className="support-card-select" ref={rootRef}>
      <button
        type="button"
        className={`support-card-select-trigger ${open ? "is-open" : ""}`}
        onClick={() => setOpen((current) => !current)}
      >
        <span>
          <small>{label}</small>
          <strong className={selected ? "" : "is-placeholder"}>
            {selected?.label ?? placeholder}
          </strong>
        </span>
        <ChevronDown size={17} />
      </button>

      {open && (
        <div className="support-card-select-menu">
          {options.map((option) => (
            <button
              key={option.value}
              type="button"
              className={option.value === value ? "is-selected" : ""}
              onClick={() => {
                onChange(option.value);
                setOpen(false);
              }}
            >
              <strong>{option.label}</strong>
              {option.hint && <span>{option.hint}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const CreateTickets = () => {
  const navigate = useNavigate();

  const [category, setCategory] = useState("");
  const [subject, setSubject] = useState("");
  const [severity, setSeverity] = useState("");
  const [message, setMessage] = useState("");
  const [attachments, setAttachments] = useState<PendingAttachment[]>(
    [],
  );
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const attachmentIcon = (attachment: TicketAttachment) => {
    if (attachment.kind === "image") {
      return <FileImage size={18} />;
    }
    if (attachment.kind === "video") {
      return <Film size={18} />;
    }
    return <File size={18} />;
  };

  const handleFiles = (files: FileList | null) => {
    if (!files) return;

    setAttachments((current) => [
      ...current,
      ...Array.from(files).map((file) => ({
        file,
        preview: makeAttachmentFromFile(file),
      })),
    ]);
  };

  const createTicket = async (event: FormEvent) => {
    event.preventDefault();

    if (!category || !subject || !severity || !message.trim()) {
      setError(
        "Please complete category, subject, severity and message.",
      );
      return;
    }

    setSubmitting(true);
    setError("");

    try {
      const ticket = await createTicketApi({
        category,
        subject,
        severity,
        message: message.trim(),
      });

      // Upload image attachments (backend accepts images only)
      const images = attachments.filter(
        (item) => item.preview.kind === "image",
      );
      if (images.length) {
        const uploaded = await Promise.all(
          images.map(async (item) => {
            const result = await uploadTicketImage(
              ticket.ticketId,
              item.file,
            );
            return {
              url: result.fileUrl,
              fileName: result.fileName || item.file.name,
              fileType: item.file.type,
              fileSize: item.file.size,
            };
          }),
        );
        await replyToTicket(
          ticket.ticketId,
          "Attachment sent.",
          uploaded,
        );
      }

      navigate(
        `/TicketMessage/${encodeURIComponent(ticket.ticketId)}`,
      );
    } catch (err: any) {
      setError(
        err?.response?.data?.message ||
          "Failed to create ticket. Please try again.",
      );
      setSubmitting(false);
    }
  };

  return (
    <section className="support-page">
      <div className="support-shell support-shell-create">
        <header className="support-page-header">
          <div className="support-heading">
            <button
              type="button"
              className="support-back-button"
              onClick={() => navigate("/Tickets")}
              aria-label="Back to Chat List"
            >
              <ArrowLeft size={16} />
            </button>

            <div>
              <p className="support-eyebrow">Support request</p>
              <h1>Create Ticket</h1>
              <p>
                Tell us what happened and attach supporting files if
                required.
              </p>
            </div>
          </div>

          <div className="support-create-header-icon">
            <TicketCheck size={22} />
          </div>
        </header>

        <form
          className="support-create-card"
          onSubmit={createTicket}
        >
          <div className="support-create-grid">
            <SupportCardSelect
              label="Category"
              placeholder="Select category"
              value={category}
              options={categoryOptions}
              onChange={setCategory}
            />

            <SupportCardSelect
              label="Subject"
              placeholder="Select subject"
              value={subject}
              options={subjectOptions}
              onChange={setSubject}
            />

            <SupportCardSelect
              label="Severity"
              placeholder="Select severity"
              value={severity}
              options={severityOptions}
              onChange={setSeverity}
            />
          </div>

          <div className="support-message-field">
            <label htmlFor="ticket-message">Message</label>
            <textarea
              id="ticket-message"
              rows={7}
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="Describe your issue clearly..."
            />
          </div>

          <div className="support-upload-card">
            <div className="support-upload-copy">
              <div className="support-upload-icon">
                <Paperclip size={20} />
              </div>
              <div>
                <strong>Attachments</strong>
                <span>
                  Add images, videos, PDF or documents.
                </span>
              </div>
            </div>

            <label className="support-upload-button">
              <Plus size={17} />
              Add files
              <input
                type="file"
                multiple
                accept="image/*,video/*,.pdf,.doc,.docx,.txt,.zip"
                onChange={(event) =>
                  handleFiles(event.target.files)
                }
              />
            </label>
          </div>

          {!!attachments.length && (
            <div className="support-create-attachments">
              {attachments.map((item) => (
                <div
                  key={item.preview.id}
                  className="support-create-attachment"
                >
                  <span className="support-file-icon">
                    {attachmentIcon(item.preview)}
                  </span>
                  <div>
                    <strong>{item.preview.name}</strong>
                    <span>{item.preview.size}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() =>
                      setAttachments((current) =>
                        current.filter(
                          (entry) =>
                            entry.preview.id !== item.preview.id,
                        ),
                      )
                    }
                    aria-label={`Remove ${item.preview.name}`}
                  >
                    <X size={15} />
                  </button>
                </div>
              ))}
            </div>
          )}

          {error && (
            <p className="support-form-error">{error}</p>
          )}

          <div className="support-create-actions">
            <button
              type="button"
              className="support-secondary-button"
              onClick={() => navigate("/Tickets")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="support-primary-button"
              disabled={submitting}
            >
              {submitting ? "Creating..." : "Create Ticket"}
            </button>
          </div>
        </form>
      </div>
    </section>
  );
};

export default CreateTickets;
